import type { Address, Party, PortalStatement } from '@erp/shared';
import Decimal from 'decimal.js';
import {
  ArrowLeft,
  Banknote,
  BarChart3,
  CalendarClock,
  CreditCard,
  FileClock,
  FolderOpen,
  Hash,
  History,
  IdCard,
  KeyRound,
  type LucideIcon,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Receipt,
  ShoppingCart,
  Tag,
  UserPlus,
  Wallet,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, Card, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/shadcn/index.js';
import { Button, EmptyState } from '../../components/ui/index.js';
import { useLayoutDirection } from '../../hooks/useDirection.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { formatDate } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';
import { WhatsAppButton } from '../common/WhatsAppButton.js';

import { CustomerKpiHeader } from './CustomerKpiHeader.js';
import { CustomerStatementCard } from './CustomerStatementCard.js';
import { PartyForm } from './PartiesPage.js';
import { PartyActivityTab, PartyFilesTab } from './PartyExtraTabs.js';
import { PartyInvoicesTab } from './PartyInvoicesTab.js';
import { PartyPaymentsTab } from './PartyPaymentsTab.js';
import { PartyReportTab } from './PartyReportTab.js';
import { PortalAccessCard } from './PortalAccessCard.js';
import {
  SupplierKpiHeader, SupplierPaymentDialog, SupplierPaymentsTab, SupplierPurchasesTab, useSupplierDocs,
} from './SupplierHub.js';

/**
 * Customer / vendor profile — the detail view behind each row in PartiesPage.
 * Shows identity, contact, commercial terms and addresses, with inline edit
 * and activate/deactivate. Reuses the list page's <PartyForm/> for editing.
 */
export function PartyDetailPage(): JSX.Element {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation('parties');
  const { t: tc } = useTranslation();
  const dir = useLayoutDirection();
  const canWriteAll = usePermission('parties.write');
  const canWriteCustomers = usePermission('customers.write');
  const companyName = useAuthStore((s) => s.tenant?.name ?? '');
  // Back-link + list context follow the route the detail was opened from: the
  // suppliers page (AP) vs the customers page (AR). A 'both' party may be reached
  // from either — the URL, not the party type, decides where "back" goes.
  const fromSuppliers = useLocation().pathname.startsWith('/purchasing/suppliers');
  const listPath = fromSuppliers ? '/purchasing/suppliers' : '/sales/parties';

  const [party, setParty] = useState<Party | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('overview');
  // Customer activity (invoices + payments) — lazily loaded when the Sales /
  // Payments tab is first opened, from the all-time account statement.
  const [statement, setStatement] = useState<PortalStatement | null>(null);
  const [stLoading, setStLoading] = useState(false);
  // Supplier (AP) statement — drives the vendor KPI strip, loaded eagerly for
  // vendors because the KPIs sit above the tabs.
  const [apStatement, setApStatement] = useState<PortalStatement | null>(null);
  const [apLoading, setApLoading] = useState(false);
  // Bumped after anything that moves money, so every open vendor tab + the KPI
  // strip refetch together and can never disagree with each other.
  const [refreshKey, setRefreshKey] = useState(0);
  const [payOpen, setPayOpen] = useState(false);
  const canReadFiles = usePermission('attachments.read');
  const canReadAudit = usePermission('audit_logs.read');
  // Recording a payment means creating AND posting it, so it needs both rights.
  const canWritePayment = usePermission('payments.write');
  const canPostPayment = usePermission('payments.post');
  const canRecordPayment = canWritePayment && canPostPayment;

  /**
   * `silent` refetches the party WITHOUT flipping the page into its skeleton.
   * A money refresh must not do that: the full-page skeleton unmounts the tabs
   * and the open payment dialog, destroying whatever the user was reading.
   */
  const load = async (silent = false): Promise<void> => {
    if (!silent) setLoading(true);
    setNotFound(false);
    setStatement(null);
    try {
      const r = await api().get<Party>(`/parties/${id}`);
      setParty(r.data);
    } catch {
      setNotFound(true);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Vendor AP statement for the KPI header. `?side=vendor` is what makes the
  // server read bills + outbound payments, so a 'both' party still gets AP here.
  const isVendorParty = !!party && party.partyType !== 'customer';

  // The narrow client-book code writes only when THIS party is a customer —
  // mirrors the API's scoping, so no button here walks its holder into a 403.
  const canWrite = canWriteAll || (canWriteCustomers && party?.partyType === 'customer');
  useEffect(() => {
    if (!id || !isVendorParty) return undefined;
    let active = true;
    setApLoading(true);
    void api()
      .get<PortalStatement>(`/parties/${id}/statement?side=vendor`)
      .then((r) => { if (active) setApStatement(r.data); })
      .catch(() => { /* KPI cards fall back to their empty rendering */ })
      .finally(() => { if (active) setApLoading(false); });
    return () => { active = false; };
  }, [id, isVendorParty, refreshKey]);

  // One shared snapshot of this supplier's purchase documents for the KPI strip,
  // both vendor tabs and the payment dialog — see useSupplierDocs.
  const supplierDocs = useSupplierDocs(isVendorParty ? id : null, refreshKey);

  /** Money moved — refresh the party row, the AP statement and every vendor tab. */
  const onMoneyChanged = (): void => {
    setStatement(null);
    setRefreshKey((k) => k + 1);
    void load(true);
  };

  // The customer's invoices + payments. Fetched as soon as the party is known
  // (not when a tab opens) because the KPI strip above the tabs — what is still
  // owed, what is overdue — is drawn from the same statement.
  // NOTE: `stLoading` is deliberately NOT a dependency — including it made setting
  // it re-run the effect, whose cleanup flipped `active=false` and discarded the
  // fetch result (the loader never resolved).
  useEffect(() => {
    if (!id || statement || !party) return;
    if (party.partyType === 'vendor') return;
    let active = true;
    setStLoading(true);
    void api()
      .get<PortalStatement>(`/parties/${id}/statement`)
      .then((r) => { if (active) setStatement(r.data); })
      .catch(() => { /* leave empty — the tab shows its empty state */ })
      .finally(() => { if (active) setStLoading(false); });
    return () => { active = false; };
     
  }, [id, statement, party]);

  if (loading) {
    return (
      <Card className="p-6">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="mt-4 h-40 w-full" />
      </Card>
    );
  }

  if (notFound || !party) {
    return (
      <EmptyState
        title={t('profile.notFound', { defaultValue: 'Customer not found' })}
        action={
          <Link to={listPath}>
            <Button>{t('profile.backToList', { defaultValue: 'Back to list' })}</Button>
          </Link>
        }
      />
    );
  }

  const toggleActive = async (): Promise<void> => {
    setBusy(true);
    try {
      if (party.isActive) await api().delete(`/parties/${party.id}`);
      else await api().patch(`/parties/${party.id}`, { isActive: true });
      await load();
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setBusy(false);
    }
  };

  const isCustomer = party.partyType !== 'vendor';
  const isVendor = party.partyType !== 'customer';
  const money = (a: string, c: string): string => formatMoney(a, { locale: i18n.language, currency: c });
  // Statement rows come back newest-first — head is the latest, tail the first.
  const lastSale = statement?.invoices[0]?.docDate ?? null;
  const firstSale = statement?.invoices.at(-1)?.docDate ?? null;
  const lastPayment = statement?.payments[0] ?? null;
  const openInvoiceCount = (statement?.invoices ?? []).filter((i) => new Decimal(i.balance).gt('0.0001')).length;
  const typeVariant = party.partyType === 'vendor' ? 'warning' : party.partyType === 'both' ? 'info' : 'success';
  const initials =
    party.displayName.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase() ?? '').join('') || '?';

  return (
    <div className="flex flex-col gap-5">
      <Link
        to={listPath}
        className="inline-flex w-fit items-center gap-1.5 text-sm text-fg-muted transition hover:text-fg"
      >
        <ArrowLeft className={`h-4 w-4 ${dir === 'rtl' ? 'rotate-180' : ''}`} aria-hidden />
        {fromSuppliers ? t('suppliersTitle', { defaultValue: 'Suppliers' }) : t('title')}
      </Link>

      {/* header */}
      <div className="flex flex-wrap items-start justify-between gap-4 rounded-2xl border border-border bg-gradient-to-br from-brand/5 via-bg to-bg p-5 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-brand to-accent text-xl font-bold text-brand-contrast shadow-md ring-1 ring-brand/20">
            {initials}
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-semibold tracking-tight">{party.displayName}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-fg-muted">
              <span className="rounded-md bg-bg-subtle px-2 py-0.5 font-mono text-xs">{party.code}</span>
              <Badge variant={typeVariant}>{t(`type.${party.partyType}`)}</Badge>
              <Badge variant={party.isActive ? 'success' : 'secondary'}>
                {t(`status.${party.isActive ? 'active' : 'inactive'}`)}
              </Badge>
              {party.partyType !== 'vendor' && (
                <Badge variant={party.warehouseId ? 'info' : 'secondary'} title={t('branch.help')}>
                  {party.warehouseId ? `${t('branch.label')}: ${party.warehouseName ?? '—'}` : t('branch.shared')}
                </Badge>
              )}
              {party.phone && (
                <a href={`tel:${party.phone}`} className="inline-flex items-center gap-1 text-xs text-fg-muted transition hover:text-brand" dir="ltr">
                  <Phone className="h-3 w-3" aria-hidden />{party.phone}
                </a>
              )}
            </div>
          </div>
        </div>
        {!editing && (
          <div className="flex flex-wrap gap-2">
            {party.phone && (
              <WhatsAppButton
                docType="customer"
                phone={party.phone}
                vars={{ company: companyName, customer: party.displayName }}
              />
            )}
            {canWrite && (
              <>
                <Button variant="secondary" onClick={() => setEditing(true)}>
                  <Pencil className="h-4 w-4" />
                  {t('actions.edit')}
                </Button>
                <Button variant="ghost" onClick={toggleActive} loading={busy}>
                  {party.isActive ? t('actions.deactivate') : t('actions.reactivate')}
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* KPIs sit above the tabs so the money question — what do they still owe
          us / what do we owe them — is answered before any clicking. Per
          currency, never summed across currencies. */}
      {!editing && isCustomer && (
        <CustomerKpiHeader statement={statement} loading={stLoading && !statement} party={party} />
      )}
      {!editing && isVendor && (
        <SupplierKpiHeader statement={apStatement} loading={apLoading} />
      )}
      {!editing && <BranchSummaryCard partyId={party.id} />}

      {editing ? (
        <PartyForm
          mode="edit"
          initial={party}
          onCancel={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false);
            await load();
          }}
        />
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-10">
            <TabsTrigger value="overview" className="gap-1.5">
              <IdCard className="h-4 w-4" aria-hidden />{t('tabs.overview', { defaultValue: 'Overview' })}
            </TabsTrigger>
            {isCustomer && (
              <TabsTrigger value="account" className="gap-1.5">
                <Wallet className="h-4 w-4" aria-hidden />{t('tabs.account', { defaultValue: 'Account' })}
              </TabsTrigger>
            )}
            {isCustomer && (
              <TabsTrigger value="sales" className="gap-1.5">
                <Receipt className="h-4 w-4" aria-hidden />{t('tabs.sales', { defaultValue: 'Sales' })}
              </TabsTrigger>
            )}
            {isCustomer && (
              <TabsTrigger value="payments" className="gap-1.5">
                <Banknote className="h-4 w-4" aria-hidden />{t('tabs.payments', { defaultValue: 'Payments' })}
              </TabsTrigger>
            )}
            {isCustomer && (
              <TabsTrigger value="portal" className="gap-1.5">
                <KeyRound className="h-4 w-4" aria-hidden />{t('tabs.portal', { defaultValue: 'Portal' })}
              </TabsTrigger>
            )}
            {isVendor && (
              <TabsTrigger value="supplier" className="gap-1.5">
                <Wallet className="h-4 w-4" aria-hidden />{t('tabs.supplierAccount', { defaultValue: 'Supplier account' })}
              </TabsTrigger>
            )}
            {isVendor && (
              <TabsTrigger value="purchases" className="gap-1.5">
                <ShoppingCart className="h-4 w-4" aria-hidden />{t('tabs.purchases', { defaultValue: 'Purchases' })}
              </TabsTrigger>
            )}
            {isVendor && (
              <TabsTrigger value="supplierPayments" className="gap-1.5">
                <Banknote className="h-4 w-4" aria-hidden />{t('tabs.supplierPayments', { defaultValue: 'Payments' })}
              </TabsTrigger>
            )}
            {/* Both sides: "what has this account done, in total" is the same
                question for a customer and a supplier, only the words differ. */}
            <TabsTrigger value="report" className="gap-1.5">
              <BarChart3 className="h-4 w-4" aria-hidden />{t('tabs.report', { defaultValue: 'Report' })}
            </TabsTrigger>
            {canReadFiles && (
              <TabsTrigger value="files" className="gap-1.5">
                <FolderOpen className="h-4 w-4" aria-hidden />{t('tabs.files', { defaultValue: 'Files' })}
              </TabsTrigger>
            )}
            {canReadAudit && (
              <TabsTrigger value="activity" className="gap-1.5">
                <History className="h-4 w-4" aria-hidden />{t('tabs.activity', { defaultValue: 'Activity' })}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="overview">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <Card>
                <SectionTitle icon={IdCard}>{t('profile.contact', { defaultValue: 'Contact' })}</SectionTitle>
                <dl className="flex flex-col gap-0.5 text-sm">
                  {/* Customers are phone-identified; email is a vendor/AP-contact field. */}
                  {!isCustomer && (
                    <InfoRow icon={Mail} label={t('create.email')} value={party.email} href={party.email ? `mailto:${party.email}` : undefined} />
                  )}
                  <InfoRow icon={Phone} label={t('create.phone')} value={party.phone} href={party.phone ? `tel:${party.phone}` : undefined} />
                  <InfoRow icon={Hash} label={t('create.taxId')} value={party.taxId} />
                  {/* Whether this customer can sign in to the shop app at all —
                      the question staff ask when a customer says "I can't log in". */}
                  {isCustomer && party.portalEnabled !== undefined && (
                    <InfoRow
                      icon={KeyRound}
                      label={t('profile.appLogin', { defaultValue: 'App login' })}
                      value={party.portalEnabled
                        ? (party.isAppManager
                          ? t('profile.appLoginManager', { defaultValue: 'Yes — shop manager view' })
                          : t('profile.appLoginYes', { defaultValue: 'Yes' }))
                        : t('profile.appLoginNo', { defaultValue: 'No account' })}
                    />
                  )}
                </dl>
              </Card>

              <Card>
                <SectionTitle icon={CreditCard}>{t('profile.terms', { defaultValue: 'Commercial terms' })}</SectionTitle>
                <dl className="flex flex-col gap-0.5 text-sm">
                  {/* The customer's group decides the prices they are charged —
                      and the currency they are charged in. It belongs next to
                      the credit terms, not buried in the edit form. */}
                  {isCustomer && (
                    <InfoRow
                      icon={Tag}
                      label={t('profile.customerType', { defaultValue: 'Customer type' })}
                      value={party.priceListName
                        ? `${party.priceListName}${party.priceListCurrencyCode ? ` · ${party.priceListCurrencyCode}` : ''}`
                        : null}
                    />
                  )}
                  <InfoRow
                    icon={CalendarClock}
                    label={t('create.paymentTermsDays')}
                    value={`${party.paymentTermsDays} ${t('profile.days', { defaultValue: 'days' })}`}
                  />
                  <InfoRow
                    icon={CreditCard}
                    label={t('profile.creditLimit', { defaultValue: 'Credit limit' })}
                    value={party.creditLimit && party.creditCurrencyCode ? formatMoney(party.creditLimit, { locale: i18n.language, currency: party.creditCurrencyCode }) : null}
                  />
                  {/* What they may still buy on credit: the limit minus what is
                      already outstanding IN THE LIMIT'S OWN CURRENCY. Balances in
                      other currencies are never netted into it. */}
                  {isCustomer && party.creditLimit && Number(party.creditLimit) > 0 && party.creditCurrencyCode && (
                    <InfoRow
                      icon={Wallet}
                      label={t('profile.creditAvailable', { defaultValue: 'Credit available' })}
                      value={statement
                        ? money(
                          Decimal.max(
                            new Decimal(party.creditLimit).minus(
                              statement.summary.find((s) => s.currencyCode === party.creditCurrencyCode)?.balance ?? '0',
                            ),
                            0,
                          ).toFixed(4),
                          party.creditCurrencyCode,
                        )
                        : null}
                    />
                  )}
                  <InfoRow
                    icon={CalendarClock}
                    label={t('profile.since', { defaultValue: 'On file since' })}
                    value={formatDate(party.createdAt, { locale: i18n.language })}
                  />
                  <InfoRow
                    icon={UserPlus}
                    label={t('profile.addedBy', { defaultValue: 'Added by' })}
                    value={party.createdByName}
                  />
                </dl>
              </Card>

              {/* The trading history in five lines: how long they've been buying,
                  how much of it is still open, and when money last came in. The
                  amounts themselves live in the KPI strip above. */}
              {isCustomer && (
                <Card>
                  <SectionTitle icon={Receipt}>{t('profile.salesSnapshot', { defaultValue: 'Sales snapshot' })}</SectionTitle>
                  {stLoading && !statement ? (
                    <Skeleton className="h-32 w-full" />
                  ) : (
                    <dl className="flex flex-col gap-0.5 text-sm">
                      <InfoRow
                        icon={Receipt}
                        label={t('profile.invoiceCount', { defaultValue: 'Invoices' })}
                        value={statement ? String(statement.invoices.length) : null}
                      />
                      <InfoRow
                        icon={FileClock}
                        label={t('profile.openInvoices', { defaultValue: 'Still open' })}
                        value={statement ? String(openInvoiceCount) : null}
                      />
                      <InfoRow
                        icon={CalendarClock}
                        label={t('profile.firstPurchase', { defaultValue: 'First purchase' })}
                        value={firstSale ? formatDate(firstSale, { locale: i18n.language }) : null}
                      />
                      <InfoRow
                        icon={CalendarClock}
                        label={t('profile.lastPurchase', { defaultValue: 'Last purchase' })}
                        value={lastSale ? formatDate(lastSale, { locale: i18n.language }) : null}
                      />
                      <InfoRow
                        icon={Banknote}
                        label={t('profile.lastPayment', { defaultValue: 'Last payment' })}
                        value={lastPayment
                          ? `${money(lastPayment.amount, lastPayment.currencyCode)} · ${formatDate(lastPayment.paymentDate, { locale: i18n.language })}`
                          : null}
                      />
                    </dl>
                  )}
                </Card>
              )}

              <Card>
                <SectionTitle icon={MapPin}>{t('profile.addresses', { defaultValue: 'Addresses' })}</SectionTitle>
                <div className="flex flex-col gap-4 text-sm">
                  <AddressBlock label={t('profile.billing', { defaultValue: 'Billing' })} addr={party.billingAddress} empty={t('profile.noAddress', { defaultValue: 'Not set' })} />
                  <AddressBlock label={t('profile.shipping', { defaultValue: 'Shipping' })} addr={party.shippingAddress} empty={t('profile.noAddress', { defaultValue: 'Not set' })} />
                </div>
              </Card>
            </div>

            {party.latitude != null && party.longitude != null && (
              <Card className="mt-4 overflow-hidden p-0">
                <div className="flex items-center justify-between gap-2 px-5 pt-4">
                  <SectionTitle>{t('profile.location', { defaultValue: 'Location' })}</SectionTitle>
                  <a
                    href={`https://www.openstreetmap.org/?mlat=${party.latitude}&mlon=${party.longitude}#map=15/${party.latitude}/${party.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
                  >
                    <MapPin className="h-3.5 w-3.5" aria-hidden />
                    {t('profile.viewOnMap', { defaultValue: 'View larger map' })}
                  </a>
                </div>
                <iframe
                  title={t('profile.location', { defaultValue: 'Location' })}
                  className="mt-2 h-72 w-full border-0"
                  loading="lazy"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${party.longitude - 0.01}%2C${party.latitude - 0.01}%2C${party.longitude + 0.01}%2C${party.latitude + 0.01}&layer=mapnik&marker=${party.latitude}%2C${party.longitude}`}
                />
              </Card>
            )}
          </TabsContent>

          {isCustomer && (
            <TabsContent value="account">
              <CustomerStatementCard party={party} onChanged={() => { setStatement(null); void load(); }} />
            </TabsContent>
          )}

          {isCustomer && (
            <TabsContent value="sales">
              <PartyInvoicesTab
                partyId={party.id}
                fallback={statement?.invoices ?? []}
                loading={stLoading}
              />
            </TabsContent>
          )}

          {isCustomer && (
            <TabsContent value="payments">
              <PartyPaymentsTab
                partyId={party.id}
                fallback={statement?.payments ?? []}
                loading={stLoading}
              />
            </TabsContent>
          )}

          <TabsContent value="report">
            {/* Two statements exist on this page and only one is fetched for a
                given party: AR is skipped for a pure vendor, AP is only loaded
                for one. Reading the wrong one would render "nothing to report"
                for a supplier with years of history. A 'both' party has both,
                and reports as a customer — which is the side the profile is
                opened from. */}
            <PartyReportTab
              statement={party.partyType === 'vendor' ? apStatement : statement}
              loading={party.partyType === 'vendor' ? apLoading : stLoading}
              side={party.partyType === 'vendor' ? 'vendor' : 'customer'}
            />
          </TabsContent>

          {isCustomer && (
            <TabsContent value="portal">
              <PortalAccessCard partyId={party.id} phone={party.phone} />
            </TabsContent>
          )}

          {isVendor && (
            <TabsContent value="supplier">
              <CustomerStatementCard
                party={party}
                side="vendor"
                onChanged={onMoneyChanged}
                // The supplier dialog lives on the page (it needs the shared
                // open-bill snapshot, not the statement's looser bill list).
                onRecordPayment={canRecordPayment ? () => setPayOpen(true) : undefined}
              />
            </TabsContent>
          )}

          {/* Radix unmounts inactive TabsContent, so each of these fetches only
              when its tab is first opened — and again whenever refreshKey moves. */}
          {isVendor && (
            <TabsContent value="purchases">
              <SupplierPurchasesTab party={party} docs={supplierDocs} />
            </TabsContent>
          )}

          {isVendor && (
            <TabsContent value="supplierPayments">
              <SupplierPaymentsTab
                party={party}
                docs={supplierDocs}
                refreshKey={refreshKey}
                onRecordPayment={canRecordPayment ? () => setPayOpen(true) : null}
              />
            </TabsContent>
          )}

          {canReadFiles && (
            <TabsContent value="files">
              <PartyFilesTab partyId={party.id} />
            </TabsContent>
          )}

          {canReadAudit && (
            <TabsContent value="activity">
              <PartyActivityTab partyId={party.id} />
            </TabsContent>
          )}
        </Tabs>
      )}

      {payOpen && (
        <SupplierPaymentDialog
          party={party}
          openBills={supplierDocs.openBills}
          onClose={() => setPayOpen(false)}
          onDone={onMoneyChanged}
        />
      )}
    </div>
  );
}

function SectionTitle({ icon: Icon, children }: { icon?: LucideIcon; children: React.ReactNode }): JSX.Element {
  return (
    <h2 className="mb-4 flex items-center gap-2 border-b border-border/60 pb-2.5 text-[0.7rem] font-bold uppercase tracking-wider text-fg-subtle">
      {Icon && <Icon className="h-3.5 w-3.5 text-brand" aria-hidden />}
      {children}
    </h2>
  );
}

function InfoRow({
  icon: Icon,
  label,
  value,
  href,
}: {
  icon: LucideIcon;
  label: string;
  value?: string | null;
  href?: string;
}): JSX.Element {
  const isLink = Boolean(value && href);
  return (
    <div className="group flex items-center gap-3 rounded-lg px-1 py-1.5 transition-colors hover:bg-bg-subtle/60">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-bg-subtle text-fg-subtle transition-colors group-hover:bg-brand/10 group-hover:text-brand">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <dt className="text-[0.68rem] font-medium uppercase tracking-wide text-fg-subtle">{label}</dt>
        <dd className="truncate text-sm font-semibold text-fg">
          {value ? (
            isLink ? (
              <a href={href} className="underline-offset-4 transition-colors hover:text-brand hover:underline" dir="ltr">
                {value}
              </a>
            ) : (
              value
            )
          ) : (
            <span className="font-normal text-fg-muted">—</span>
          )}
        </dd>
      </div>
    </div>
  );
}

function AddressBlock({
  label,
  addr,
  empty,
}: {
  label: string;
  addr?: Address | null;
  empty: string;
}): JSX.Element {
  return (
    <div>
      <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-fg-muted">
        <MapPin className="h-3.5 w-3.5" aria-hidden />
        {label}
      </div>
      {addr ? (
        <address className="not-italic leading-relaxed text-fg">
          {addr.line1}
          {addr.line2 ? `, ${addr.line2}` : ''}
          <br />
          {[addr.city, addr.region, addr.postalCode].filter(Boolean).join(', ')}
          <br />
          {addr.country}
        </address>
      ) : (
        <span className="text-fg-muted">{empty}</span>
      )}
    </div>
  );
}

/**
 * Per-branch money for this party — "his debt in each branch", the owner's
 * question answered in one card. Server groups outstanding documents by their
 * header warehouse (invoice-based, like every debt figure here); NULL groups
 * as "unbranched". Hidden entirely for single-branch shops (nothing to split).
 */
function BranchSummaryCard({ partyId }: { partyId: string }): JSX.Element | null {
  const { t, i18n } = useTranslation('parties');
  const [rows, setRows] = useState<Array<{
    warehouseId: string | null; warehouseName: string | null; currencyCode: string;
    invoiced: string; paid: string; balance: string; docCount: number;
  }> | null>(null);

  useEffect(() => {
    let on = true;
    void api().get<{ items: NonNullable<typeof rows> }>(`/parties/${partyId}/branch-summary`)
      .then((r) => { if (on) setRows(r.data.items); })
      .catch(() => { if (on) setRows([]); });
    return () => { on = false; };
  }, [partyId]);

  if (!rows || rows.length === 0) return null;
  // One branch (or only unbranched rows) = nothing to compare — no card.
  const distinct = new Set(rows.map((r) => r.warehouseId ?? ''));
  if (distinct.size < 2) return null;

  const money = (v: string, ccy: string): string => formatMoney(v, { locale: i18n.language, currency: ccy });
  return (
    <Card className="overflow-hidden p-0">
      <h2 className="border-b border-border px-4 py-3 text-sm font-semibold">
        {t('branches.title', { defaultValue: 'By branch' })}
      </h2>
      <table className="w-full text-sm">
        <thead className="bg-bg-subtle text-fg-muted">
          <tr>
            <th className="px-4 py-2 text-start font-medium">{t('branches.branch', { defaultValue: 'Branch' })}</th>
            <th className="px-4 py-2 text-end font-medium">{t('branches.invoiced', { defaultValue: 'Invoiced' })}</th>
            <th className="px-4 py-2 text-end font-medium">{t('branches.paid', { defaultValue: 'Paid' })}</th>
            <th className="px-4 py-2 text-end font-medium">{t('branches.balance', { defaultValue: 'Balance' })}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border">
              <td className="px-4 py-2">
                {r.warehouseName ?? t('branches.none', { defaultValue: 'Unbranched' })}
                <span className="ms-2 text-xs text-fg-muted">({r.docCount})</span>
              </td>
              <td className="px-4 py-2 text-end font-mono tabular-nums">{money(r.invoiced, r.currencyCode)}</td>
              <td className="px-4 py-2 text-end font-mono tabular-nums">{money(r.paid, r.currencyCode)}</td>
              <td className={`px-4 py-2 text-end font-mono tabular-nums font-semibold ${Number(r.balance) > 0 ? 'text-danger' : ''}`}>
                {money(r.balance, r.currencyCode)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
